export interface OrderItem {
  id: number;
  productId: string;
  productName: string;
  productImageUrl?: string;
  price: number;
  quantity: number;
  totalPrice: number;
  scale?: string;
}

export interface TrackingEvent {
  date: string;
  status: string;
  description: string;
  location?: string;
}

export interface OrderTracking {
  trackingNumber?: string;
  status: string;
  shippedDate?: string;
  deliveredDate?: string;

  // Estimated production timelines
  estimatedPrintEndDate?: string;
  estimatedReadyDate?: string;
  estimatedPrintingDays?: number;
  estimatedPreparationDays?: number;
  events: TrackingEvent[];
}

export interface Order {
  id: number;
  orderNumber: string;
  orderDate: string;
  totalAmount: number;
  shippingCost: number;
  taxAmount: number;
  status: string;
  paymentStatus: string;
  notes?: string;

  // Shipping Information
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  shippingCountry: string;
  shippingPhoneNumber?: string;
  trackingNumber?: string;
  shippedDate?: string;
  deliveredDate?: string;

  createdAt: string;
  updatedAt: string;

  items: OrderItem[];
  tracking?: OrderTracking;
}

export interface OrderList {
  orders: Order[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface OrderSearch {
  orderNumber?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export interface UpdateShippingInfoRequest {
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  shippingCountry: string;
  shippingPhoneNumber: string;
}

export interface UpdateGuestShippingInfoRequest extends UpdateShippingInfoRequest {
  orderNumber: string;
  email: string;
}

export const OrderStatuses = [
  'Pending',
  'Processing',
  'Printing',
  'Printed',
  'Shipped',
  'Delivered',
  'Cancelled',
  'Refunded',
] as const;

export type OrderStatus = (typeof OrderStatuses)[number];

export interface OrderStatusObject {
  id: string;
  name: string;
}

// Guest order types
export interface CreateGuestOrderRequest {
  cartId: string;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  shippingCountry: string;
  notes?: string;
  guestEmail: string;
  guestPhoneNumber: string;
}

// Regular order types (for authenticated users)
export interface CreateOrderRequest {
  cartId: string;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  shippingCountry: string;
  notes?: string;
  shippingPhoneNumber: string;
  // Guest fields are optional and ignored for authenticated users
  guestEmail?: string;
  guestPhoneNumber?: string;
}

export interface OrderEstimateRequest {
  cartId: string;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  shippingCountry: string;
}

export interface OrderEstimateResponse {
  subtotal: number;
  shippingCost: number;
  taxAmount: number;
  totalAmount: number;
}

export interface GuestOrderTracking {
  id: number;
  orderNumber: string;
  orderDate: string;
  totalAmount: number;
  shippingCost: number;
  taxAmount: number;
  status: string;
  paymentStatus: string;

  // Shipping Information
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  shippingCountry: string;
  shippingPhoneNumber?: string;
  trackingNumber?: string;
  shippedDate?: string;
  deliveredDate?: string;

  items: OrderItem[];
  tracking?: OrderTracking;
}

export interface GuestOrderLookupRequest {
  orderNumber: string;
  email: string;
}
